import React, { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'
import { values } from 'lodash'
import { Button, DatePicker, Divider, message } from 'antd'
import { faBriefcase, faCalendar, faCheck, faClinicMedical, faGraduationCap, faSchool, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'

import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import { isValid } from '../../../utils/validators'
import { EUROPEAN_DATE } from '../../../utils/constants'
import { selectUser } from '../../../reducers/UserReducer'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { getAllSchools } from '../../../utils/api/school'
import { getAllInstitutions } from '../../../utils/api/institution'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isObject } from '../../../utils'
import { getAllAssistants } from '../../../utils/api/assistant'
import WorkingRegimesSelector from '../../../Components/shared/Selectors/WorkingRegimesSelector'
import SpecialitiesSelector from '../../../Components/shared/Selectors/SpecialitiesSelector'
import AssistantsSelector from '../../../Components/shared/Selectors/AssistantsSelector'
import { MaccsInternship, MaccsInternshipProperties } from '../../../utils/entities/maccsInternship'
import FormItem from '../../../Components/shared/Forms/FormItem'
import { isInstitutionGroupAdmin, isSchoolMaccsUser } from '../../../utils/roles'

const COLUMN_WIDTH = '350px'
const FIELD_WIDTH = '350px'
const { START_DATE, END_DATE, SCHOOL, INSTITUTION, ASSISTANT, WORKING_REGIME, SPECIALITY } = MaccsInternshipProperties

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const MaccsInternshipForm = ({ data, sectors, validators, onSubmit, onCancel }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [error, setError] = useState({ missingFields: [] })
  const [localData, setLocalData] = useState({})
  const [assistants, setAssistants] = useState({ data: [], loading: false })
  const [schools, setSchools] = useState({ data: [], loading: false })
  const [institutions, setInstitutions] = useState({ data: [], loading: false })

  useEffect(() => {
    if (isObject(data)) {
      setLocalData(data)
    }
  }, [data])

  useEffect(() => {
    getAllInstitutions(user).then(({ data }) => {
      setInstitutions({ data, loading: false })
    })
    getAllAssistants(user).then(({ data }) => {
      setAssistants({ data, loading: false })
    })
    getAllSchools(user).then(({ data }) => {
      setSchools({ data, loading: false })
    })
  }, [user.id])

  const handleSubmit = (e) => {
    const errors = isValid(localData, values(validators))

    if (localData.startDate > localData.endDate) {
      message.warning(t('internship_form.error_dates'))
    } else if (errors.keys.length === 0) {
      onSubmit(new MaccsInternship(localData))
    }

    setError({
      ...error,
      title: t('internship_form.error_field'),
      visible: true,
      missingFields: errors.keys
    })
  }

  const getStyle = key => {
    const style = { width: FIELD_WIDTH }

    if (error.missingFields.includes(key)) {
      style.border = '2px solid red'
      style.borderRadius = '4px'
    }

    return style
  }

  return (
    <div className='flex-column'>
      <div className='form-class' style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
        {/* Colonne de gauche */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem icon={faCalendar} label={t('common.start_date')} required>
            <DatePicker
              disabled={!isSchoolMaccsUser(user)}
              value={localData.startDate}
              onChange={startDate => setLocalData({ ...localData, startDate })}
              format={EUROPEAN_DATE}
              style={getStyle(START_DATE)}
            />
          </FormItem>
          <FormItem icon={faSchool} label={t('common.school')}>
            <SchoolsSelector
              disabled={!isSchoolMaccsUser(user)}
              multiple='default'
              schools={schools.data}
              values={getIdOrNull(localData.school)}
              onChange={school => setLocalData({ ...localData, school, speciality: null })}
              style={getStyle(SCHOOL)}
            />
          </FormItem>
          <FormItem icon={faGraduationCap} label={t('Speciality')}>
            <SpecialitiesSelector
              values={getIdOrNull(localData.speciality)}
              onChange={speciality => setLocalData(prev => ({ ...prev, speciality }))}
              style={getStyle(SPECIALITY)}
            />
          </FormItem>
          {(
            <FormItem icon={faUserGraduate} label={t('Assistant')}>
              <AssistantsSelector
                disabled={!isSchoolMaccsUser(user)}
                assistants={assistants.data}
                values={getIdOrNull(localData.assistant)}
                onChange={assistant => setLocalData({ ...localData, assistant })}
                style={getStyle(ASSISTANT)}
                loading={assistants.loading}
              />
            </FormItem>
          )}
        </div>

        {/* Barre verticale */}
        <div
          style={{
            width: '1px',
            background: 'linear-gradient(to bottom, transparent, #d9d9d9, transparent)',
            alignSelf: 'stretch',
            margin: '0 8px'
          }}
        />

        {/* Colonne de droite */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem icon={faCalendar} label={t('common.end_date')} required>
            <DatePicker
              disabled={!isSchoolMaccsUser(user)}
              value={localData.endDate}
              onChange={endDate => setLocalData({ ...localData, endDate })}
              format={EUROPEAN_DATE}
              style={getStyle(END_DATE)}
            />
          </FormItem>
          <FormItem icon={faClinicMedical} label={t('common.institution')}>
            <InstitutionsSelector
              disabled={!isSchoolMaccsUser(user) && !isInstitutionGroupAdmin(user)}
              multiple='default'
              values={getIdOrNull(localData.institution)}
              institutions={institutions.data}
              onChange={institution => setLocalData({ ...localData, institution })}
              style={getStyle(INSTITUTION)}
            />
          </FormItem>
          <FormItem icon={faBriefcase} label={t('Working regime')}>
            <WorkingRegimesSelector
              disabled={!isSchoolMaccsUser(user)}
              values={getIdOrNull(localData.workingRegime)}
              onChange={workingRegime => setLocalData({ ...localData, workingRegime })}
              style={getStyle(WORKING_REGIME)}
            />
          </FormItem>
        </div>
      </div>
      <div className='modal-footer'>
        <Divider />
        <div className='form-footer flex-row aln-ctr jst-end'>
          <Button key='cancel' type='default' onClick={onCancel}> {t('common.cancel')} </Button>
          <IconButton key='save' type='primary' onClick={handleSubmit} icon={faCheck} text={t('common.save')} />
        </div>
      </div>
    </div>
  )
}

export default MaccsInternshipForm
